/**
 * 判分器注册表
 * ------------------------------------------------------------
 * 每个题型一个判分函数，统一签名：
 *   grader(question, response) -> { correct, partial?, expected, detail }
 *
 * 新增题型：在 src/engines/graders/ 下新建文件调用 registerGrader()，
 * 并在 graders/index.js 里 import 一次即可。
 */
import { normalizeEn, normalizeZh, tokenize } from '../../utils/index.js';

const graders = new Map();

export function registerGrader(type, fn) {
  graders.set(type, fn);
  return fn;
}

export function getGrader(type) {
  return graders.get(type) || gradeFill;
}

export function graderTypes() {
  return [...graders.keys()];
}

/** 统一入口：容错，未知题型按填空处理 */
export function grade(question, response) {
  const fn = getGrader(question.type);
  try {
    const res = fn(question, response);
    return { partial: res.correct ? 1 : 0, detail: '', ...res };
  } catch (err) {
    console.warn('[grader] 判分异常', err, question);
    return { correct: false, expected: question.answer?.[0] || '', detail: '判分异常' };
  }
}

/* --------------------------- 内置判分实现 --------------------------- */

/** 选择 / 听音选择：完全匹配选项文本 */
export function gradeChoice(q, response) {
  const answers = (q.answer || []).map(normalizeEn);
  const got = normalizeEn(response);
  const correct = answers.includes(got);
  return {
    correct,
    expected: q.answer?.[0] || '',
    detail: correct ? '' : `正确答案：${q.answer?.[0] ?? ''}`,
  };
}

/** 判断：选项为「正确 / 错误」 */
export const gradeJudge = gradeChoice;

/** 填空：支持多空（答案用 | 分隔），逐空计分 */
export function gradeFill(q, response) {
  const gotParts = String(response ?? '').split('|||').map(s => s.trim());
  const answers = (q.answer || []).map(a => String(a).split('|').map(s => s.trim()));
  let hit = 0;
  const expected = [];
  answers.forEach((alts, i) => {
    const got = gotParts[i] ?? '';
    expected.push(alts[0]);
    const ok = alts.some(a => normalizeEn(a) === normalizeEn(got) || normalizeZh(a) === normalizeZh(got))
      || (q.accept || []).some(a => normalizeEn(a) === normalizeEn(got));
    if (ok) hit++;
  });
  const correct = answers.length > 0 && hit === answers.length;
  return {
    correct,
    partial: answers.length ? hit / answers.length : 0,
    expected: expected.join(' / '),
    detail: correct ? '' : `正确答案：${expected.join(' / ')}`,
  };
}

/** 改错：整句比对 */
export function gradeCorrect(q, response) {
  const correct = (q.answer || []).some(a => normalizeEn(a) === normalizeEn(response));
  return {
    correct,
    expected: q.answer?.[0] || '',
    detail: correct ? '' : `正确写法：${q.answer?.[0] ?? ''}`,
  };
}

/** 句子转换 */
export function gradeTransform(q, response) {
  const correct = (q.answer || []).some(a => normalizeEn(a) === normalizeEn(response));
  return {
    correct,
    expected: q.answer?.[0] || '',
    detail: correct ? '' : `参考答案：${q.answer?.[0] ?? ''}`,
  };
}

/** 排序：按位置比对词序列 */
export function gradeOrder(q, response) {
  const got = Array.isArray(response) ? response : String(response ?? '').split('|');
  const target = String(q.answer?.[0] ?? '').trim();
  const expect = tokenize(target);
  const hit = got.filter((w, i) => normalizeEn(w) === normalizeEn(expect[i] || '')).length;
  const correct = normalizeEn(got.join(' ')) === normalizeEn(target);
  return {
    correct,
    partial: expect.length ? hit / expect.length : 0,
    expected: target,
    detail: correct ? '' : `正确顺序：${target}`,
  };
}

/** 拼写：忽略空格/连字符/撇号与大小写 */
export function gradeSpell(q, response) {
  const strip = s => String(s ?? '').replace(/[\s\-'’]/g, '').toLowerCase();
  const correct = (q.answer || []).concat(q.accept || []).some(a => strip(a) === strip(response));
  return {
    correct,
    expected: q.answer?.[0] || '',
    detail: correct ? '' : `正确拼写：${q.answer?.[0] ?? ''}`,
  };
}

/* --------------------------- 注册内置判分器 --------------------------- */
/*
 * 新增题型时在这里追加一行即可，例如：
 *   registerGrader('translate', gradeMyTranslate);
 * 判分器缺失时 grade() 会降级为自定义填空判定，不会崩。
 */
registerGrader('choice', gradeChoice);
registerGrader('listen', gradeChoice);
registerGrader('judge', gradeJudge);
registerGrader('fill', gradeFill);
registerGrader('correct', gradeCorrect);
registerGrader('transform', gradeTransform);
registerGrader('order', gradeOrder);
registerGrader('spell', gradeSpell);
